import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import HeroV3 from "./landing/v3/HeroV3";
import StatBar from "./landing/v3/StatBar";
import SEOHead from "../components/SEOHead";

const VaultSection = lazy(() => import("./landing/v3/VaultSection"));
const LearnSection = lazy(() => import("./landing/v3/LearnSection"));
const ArenaSection = lazy(() => import("./landing/v3/ArenaSection"));
const DirectorySection = lazy(() => import("./landing/v3/DirectorySection"));
const CommunitySection = lazy(() => import("./landing/v3/CommunitySection"));

// Organization + WebSite JSON-LD lives ONCE in public/index.html (@graph) — do not
// re-inject it here or crawlers see duplicate Organization/WebSite entities.

/**
 * Public marketing landing — iter221b premium restructure.
 * 5 intentional sections:
 *   Hero · LivePulse · PipelineBento · VerificationCrucible · TheArsenal · FinalBoot
 * Live catalogue numbers (domains / tracks / labs / categories) pull from
 * /api/landing so copy never drifts.
 */
export default function LandingPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        api.get("/landing")
            .then(({ data }) => setData(data))
            .catch(() => setData({ stats: {}, puzzle: { ciphertext: "" } }))
            .finally(() => setLoading(false));
    }, []);

    // Recruiter redirect removed — hacker-first pivot.

    const stats = data?.stats || {};
    const domains = stats.academy_domains;
    const labs = stats.labs_total;
    const categories = stats.lab_categories;

    return (
        <div className="relative bg-[#05070a] text-slate-200" data-testid="landing-page" style={{ fontFamily: "'Space Grotesk', ui-sans-serif, system-ui" }}>
            <SEOHead
                title="Practice, Compete &amp; Hunt — Ethical Hacking Community"
                description={`The community for ethical hackers — ${labs ? `${labs} ` : ""}hands-on labs, live CTF competitions, cloud Kali attack boxes in your browser and a daily bug bounty directory.`}
                path="/"
                keywords="ethical hacking practice labs, CTF competitions, bug bounty programs, cybersecurity training, learn ethical hacking, capture the flag, hacker community, penetration testing practice, web exploitation labs, bug bounty directory, cloud kali in browser, pwnbox, active directory lab"
                type="website"
            />

            {/* Global grain overlay — kills the flat 'digital AI' look */}
            <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-overlay" aria-hidden="true"
                 style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

            <div className="relative z-[2]">
                <HeroV3 puzzle={data?.puzzle} loading={loading} />
                <StatBar />
                <Suspense fallback={<div className="min-h-[60vh]" aria-hidden="true" />}>
                    <LearnSection />
                    <VaultSection />
                    <ArenaSection />
                    <DirectorySection />
                    <CommunitySection />
                </Suspense>
            </div>
        </div>
    );
}
